import React, { useState } from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import { login, setLoading } from '../../store/slices/authSlice';
import { apiService } from '../../services';
import { ROUTES, APP_NAME } from '../../constants';
import { AuthResponse } from '../../types/auth.types';

export const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoading = useAppSelector(state => state.auth.isLoading);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) {
      setError('No credential received from Google');
      return;
    }

    dispatch(setLoading(true));
    setError(null);

    try {
      const authResponse = await apiService.post<AuthResponse>('/auth/google', {
        credential: response.credential,
      });

      dispatch(login({
        user: authResponse.user,
        token: authResponse.access_token,
      }));

      navigate(ROUTES.HOME);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      if (message.includes('403')) {
        setError('Your email domain is not authorized. Please use your company email.');
      } else {
        setError(message);
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div className="relative isolate overflow-hidden min-h-screen flex items-center justify-center px-4 bg-[radial-gradient(120%_140%_at_50%_-20%,#fff7ed_0%,#f8fafc_55%,#eef2f6_100%)]">
      {/* Background effects — matching landing page */}
      <div className="pointer-events-none absolute -top-24 left-1/2 h-[360px] w-[360px] -translate-x-1/2 rounded-full bg-amber-400/20 blur-[120px]" />
      <div className="pointer-events-none absolute top-48 right-[-120px] h-[320px] w-[320px] rounded-full bg-rose-400/15 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-[-120px] h-[280px] w-[280px] rounded-full bg-slate-900/10 blur-[140px]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(15,23,42,0.04)_1px,transparent_0)] [background-size:24px_24px]" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-[420px]">
        {/* Logo + App name */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center shadow-lg shadow-slate-900/20">
              <svg className="w-5 h-5 text-amber-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2m0 14v2m9-9h-2M5 12H3m14.364-6.364l-1.414 1.414M7.05 16.95l-1.414 1.414M16.95 16.95l1.414 1.414M7.05 7.05 5.636 5.636M12 8a4 4 0 100 8 4 4 0 000-8z" />
              </svg>
            </div>
            <span className="text-xl font-semibold text-slate-900 tracking-wide">{APP_NAME}</span>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Welcome back</h1>
          <p className="mt-2 text-sm text-slate-500">Sign in with your company account to continue</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl bg-white/80 border border-white/70 shadow-[0_2px_12px_rgba(0,0,0,0.06)] backdrop-blur-sm p-8">
          {/* Google button */}
          <div className="flex justify-center">
            {isLoading ? (
              <div className="flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <svg className="h-5 w-5 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm font-medium text-slate-500">Signing you in...</span>
              </div>
            ) : (
              <div className="w-full max-w-[320px]">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Google sign-in failed. Please try again.')}
                  size="large"
                  width="320"
                  text="continue_with"
                  shape="rectangular"
                  theme="outline"
                  logo_alignment="left"
                />
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <svg className="w-4 h-4 text-red-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <p className="text-xs text-red-700 leading-relaxed">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-[11px] text-slate-400">
          Only authorized company accounts can sign in.
          <br />
          <span className="text-slate-300">Powered by Job Engine</span>
        </p>
      </div>
    </div>
  );
};
